import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import axios from "axios";
import {
  ArrowLeft,
  Heart,
  Users,
  Clock,
  Flame,
  Dumbbell,
  Zap,
  Play
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_3e64acab-2561-42e6-b860-06b1a563df4d/artifacts/5nvw3yv2_6ECA6BBA-F429-4238-A743-EE6BF2D1D33F.png";

const WORKOUT_ICONS = {
  "Full Body": Dumbbell,
  "Cardio": Zap,
  "Core": Flame,
  "Flexibility": Heart
};

export default function PartnerWorkouts() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [workouts, setWorkouts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchWorkouts();
  }, []);

  const fetchWorkouts = async () => {
    try {
      const response = await axios.get(`${API}/workouts/partner`);
      setWorkouts(response.data.partner_workouts);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Subscription required");
        navigate("/pricing");
      } else {
        toast.error("Failed to load partner workouts");
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Heart className="h-12 w-12 text-pink-500 mx-auto mb-4 animate-pulse" />
          <p className="text-primary font-bold">Loading Partner Workouts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-40 bg-background/80 backdrop-blur-lg border-b border-pink-500/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/dashboard" className="flex items-center gap-2 text-muted-foreground hover:text-foreground">
              <ArrowLeft className="h-5 w-5" /> Dashboard
            </Link>
            <div className="flex items-center gap-2">
              <Heart className="h-5 w-5 text-pink-500 fill-pink-500" />
              <span className="font-bold tracking-tight">PARTNER WORKOUTS</span>
            </div>
            <div className="w-24" />
          </div>
        </div>
      </header>

      <main className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto">
        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <motion.div
            animate={{ scale: [1, 1.1, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-20 h-20 mx-auto mb-6 rounded-full bg-pink-500/20 flex items-center justify-center"
          >
            <Users className="h-10 w-10 text-pink-500" />
          </motion.div>
          <h1 className="text-4xl font-black uppercase tracking-tighter mb-2">
            <span className="text-pink-500">Couples</span> Workouts
          </h1>
          <p className="text-muted-foreground max-w-md mx-auto">
            Sweat together, stay together. Partner exercises designed to build strength and connection.
          </p>
        </motion.div>

        {/* Workout List */}
        <div className="space-y-4">
          {workouts.map((workout, index) => {
            const IconComponent = WORKOUT_ICONS[workout.focus_area] || Dumbbell;
            
            return (
              <motion.div
                key={workout.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="bg-card border-border hover:border-pink-500/50 transition-all group">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-6">
                      {/* Icon */}
                      <div className="w-16 h-16 rounded-full bg-pink-500/20 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                        <IconComponent className="h-8 w-8 text-pink-500" />
                      </div>
                      
                      {/* Info */}
                      <div className="flex-1">
                        <h3 className="text-xl font-black uppercase tracking-tight mb-1">
                          {workout.title}
                        </h3>
                        <p className="text-sm text-muted-foreground mb-3">
                          {workout.description}
                        </p>
                        <div className="flex flex-wrap gap-3">
                          <div className="flex items-center gap-1 text-sm text-muted-foreground">
                            <Clock className="h-4 w-4" />
                            <span>{workout.duration_minutes} min</span>
                          </div>
                          <div className="flex items-center gap-1 text-sm text-muted-foreground">
                            <Dumbbell className="h-4 w-4" />
                            <span>{workout.exercises.length} exercises</span>
                          </div>
                          <div className="px-2 py-0.5 bg-pink-500/20 rounded-full">
                            <span className="text-xs text-pink-400 capitalize">{workout.difficulty}</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Start Button */}
                      <Link to={`/partner-workout/${workout.id}`}>
                        <Button className="bg-pink-500 hover:bg-pink-600 rounded-full h-14 w-14 p-0 group-hover:scale-110 transition-transform">
                          <Play className="h-6 w-6 ml-1" />
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>

        {/* Tips */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-12"
        >
          <Card className="bg-gradient-to-br from-pink-500/20 to-pink-500/5 border-pink-500/30">
            <CardContent className="p-6">
              <h3 className="font-bold text-pink-500 mb-4 flex items-center gap-2">
                <Heart className="h-5 w-5" /> Partner Workout Tips
              </h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>💬 <span className="font-medium text-foreground">Communicate</span> - Talk throughout the workout about pace and form</li>
                <li>🤝 <span className="font-medium text-foreground">Support</span> - Encourage each other, especially during tough sets</li>
                <li>⚖️ <span className="font-medium text-foreground">Match Energy</span> - Sync your movements for better connection</li>
                <li>🎉 <span className="font-medium text-foreground">Celebrate</span> - High-five after each exercise!</li>
              </ul>
            </CardContent>
          </Card>
        </motion.div>
      </main>
    </div>
  );
}
